/**
 * @module mouseEventsHelper
 * @description Provides custom mouse events to attach a callback to.
 * @example MouseEventsHelper.onMouseExit(functionName, functionArgument1, functionArgument2)
 */
import {debounce} from './debounce.js';

/**
 * Register Handlers to execute a callback function
 * when the mouse exits the window boundaries.
 *
 * @param {(...args: string[]) => void} callback The function to call once
 * the mouse has left the window boundaries.
 *
 * @param {...string} args The comma-separated list of arguments
 * to pass to the callback function.
 */
function onMouseExit(callback, ...args) {
    // Initialize variable to check if the mouse has already left the window before.
    let mouseAlreadyLeft = false;
    document.body.addEventListener('mouseleave', () => {
        if (!mouseAlreadyLeft) {
            mouseAlreadyLeft = true;
            callback(...args);
        }
    });
}

/**
 * Register Handlers to execute a callback function
 * when the mouse scrolls half-way down the page.
 *
 * @param {(...args: string[]) => void} callback The function to call once
 * the user has scrolled past the first half of the page.
 *
 * @param {...string} args The comma-separated list of arguments
 * to pass to the callback function.
 */
function onMouseScrollHalfWayDownPageHeight(callback, ...args) {
    const body = document.body;
    const html = document.documentElement;
    // Initialize variable to check if the mouse has already passed half-way down the page before.
    let mouseAlreadyPassedHalfPage = false;

    const pageHeight = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight
    );

    const halfPageHeight = Math.floor(pageHeight / 2);

    const onMidScroll = debounce(() => {
        if (window.pageYOffset > halfPageHeight && !mouseAlreadyPassedHalfPage) {
            mouseAlreadyPassedHalfPage = true;
            callback(...args);
        }
    }, 50);

    window.addEventListener('scroll', onMidScroll);
}

export const MouseEventsHelper = /** @alias module:modules/mouseEventsHelper */ {
    /**
     * Register Handlers to execute a callback function
     * when the mouse exits the window boundaries.
     *
     * @param {(...args: string[]) => void} callback The function to call
     * once the mouse exits the window boundaries.
     *
     * @param {...string} args A comma-separated list of
     * arguments to pass to the callback function.
     *
     * @function
     */
    onMouseExitsWindow: onMouseExit,
    /**
     * Register Handlers to execute a callback function
     * when the mouse moves past the first half of the page.
     *
     * @param {(...args: string[]) => void} callback The function to call
     * once the mouse moves past the first half of the page.
     *
     * @param {...string} args A comma-separated list of
     * arguments to pass to the callback function.
     *
     * @function
     */
    onMouseScrollHalfWayDownPageHeight: onMouseScrollHalfWayDownPageHeight,
};
